import React, { useEffect, useState } from 'react';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import {
  createCamera,
  updateCamera,
  deleteCamera,
  detectCameraResolution,
  type CameraDto,
  type CameraCreatePayload,
} from '../../api/cameras';
import {
  createProvider,
  getProvider,
  updateProvider,
  deleteProvider,
  type CameraProviderDto,
  type CameraProviderCreatePayload,
} from '../../api/providers';
import { CAMERA_TYPE_OPTIONS, extractError } from './helpers';
import { VendorParamsPanel } from './VendorParamsPanel';
import { LiveStreamPanel } from './LiveStreamPanel';
import { PtzPad } from './PtzPad';
import { AdminTunnelButton } from './AdminTunnelButton';
import { useCurrentUser } from '@/hooks/useCurrentUser';

interface UnifiedCameraDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
  zoneId: string;
  camera?: CameraDto | null;
  provider?: CameraProviderDto | null;
  /** Pre-fill the camera name (and trassir_name) on create. Used by the
   *  LC peer-discovery flow to seed the form from a discovered host's
   *  hostname or IP. Ignored on edit. */
  initialName?: string;
  /** Pre-fill the provider IP on create. Used by the LC peer-discovery
   *  flow to seed the form from a discovered host. Ignored on edit. */
  initialIp?: string;
}

export function UnifiedCameraDialog({
  open,
  onOpenChange,
  onSaved,
  zoneId,
  camera,
  provider,
  initialName,
  initialIp,
}: UnifiedCameraDialogProps) {
  const { user } = useCurrentUser();
  const isSuperuser = user?.is_superuser === true;
  const isEdit = !!camera;

  // Camera fields
  const [name, setName] = useState('');
  const [guid, setGuid] = useState('');
  const [trassirName, setTrassirName] = useState('');
  const [type, setType] = useState('rtsp');
  const [active, setActive] = useState(true);
  const [maxXRes, setMaxXRes] = useState('');
  const [maxYRes, setMaxYRes] = useState('');

  // Provider fields
  const [ipAddress, setIpAddress] = useState('');
  const [channel, setChannel] = useState('');
  const [port, setPort] = useState('');
  const [apiPort, setApiPort] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  // Sibling Equipment cards (Router, NVR) let the operator reveal the
  // stored password with an eye toggle; the camera dialog matches that
  // pattern. CameraProviderBase serialiser returns the real value on
  // the DETAIL endpoint (list still strips it), so we fetch it once
  // when the dialog opens with an existing provider.
  const [showPassword, setShowPassword] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [codec, setCodec] = useState('h265');
  const [forceTcp, setForceTcp] = useState(false);

  // UI state
  const [saving, setSaving] = useState(false);
  const [detectingRes, setDetectingRes] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (camera) {
      setName(camera.name ?? '');
      setGuid(camera.guid ?? '');
      setTrassirName(camera.trassir_name ?? '');
      setType(camera.type ?? 'rtsp');
      setActive(camera.active ?? true);
      setMaxXRes(camera.max_x_resolution != null ? String(camera.max_x_resolution) : '');
      setMaxYRes(camera.max_y_resolution != null ? String(camera.max_y_resolution) : '');
    } else {
      // Create path — accept caller-supplied seeds (LC peer-discovery
      // pre-fills name + IP from the discovered host).
      const seedName = initialName?.trim() ?? '';
      setName(seedName);
      setGuid(''); setTrassirName(seedName);
      setType('rtsp'); setActive(true);
      setMaxXRes(''); setMaxYRes('');
    }
    setShowPassword(false);
    if (provider) {
      setIpAddress(provider.ip_address);
      setChannel(provider.channel);
      setPort(provider.port != null ? String(provider.port) : '');
      setApiPort(String(provider.api_port));
      setUsername(provider.username ?? '');
      // List endpoint excludes password — fetch the detail to populate
      // the input so the operator can reveal/copy the stored value.
      setPassword('');
      setLoadingDetail(true);
      let cancelled = false;
      void getProvider(provider.id)
        .then(r => { if (!cancelled) setPassword(r.data.password ?? ''); })
        .catch(() => { /* keep input empty on fetch failure */ })
        .finally(() => { if (!cancelled) setLoadingDetail(false); });
      setCodec(provider.codec ?? 'h265');
      setForceTcp(provider.force_tcp ?? false);
      return () => { cancelled = true; };
    } else {
      // Pre-fill the IP from the caller's seed (LC peer-discovery), keep
      // everything else operator-typed. Channel + ports left empty so
      // the operator sees an explicit empty state.
      setIpAddress(initialIp?.trim() ?? '');
      setChannel(''); setPort(''); setApiPort('');
      setUsername(''); setPassword('');
      setCodec('h265'); setForceTcp(false);
    }
    setError(null);
  }, [open, camera, provider, initialName, initialIp]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      let cameraId: string | number;
      if (isEdit) {
        await updateCamera(camera!.id, {
          name: name.trim(),
          type,
          active,
          max_x_resolution: maxXRes ? Number(maxXRes) : undefined,
          max_y_resolution: maxYRes ? Number(maxYRes) : undefined,
        });
        cameraId = camera!.id;
      } else {
        const payload: CameraCreatePayload = {
          name: name.trim(),
          guid: guid.trim(),
          trassir_name: trassirName.trim(),
          type,
          active,
          zone: Number(zoneId),
          max_x_resolution: maxXRes ? Number(maxXRes) : undefined,
          max_y_resolution: maxYRes ? Number(maxYRes) : undefined,
        };
        const resp = await createCamera(payload);
        cameraId = resp.data.id;
      }

      const hasProviderFields = ipAddress.trim() || channel.trim();
      if (hasProviderFields) {
        const providerPayload: CameraProviderCreatePayload = {
          camera: Number(cameraId),
          name: name.trim() || 'Подключение',
          ip_address: ipAddress.trim(),
          channel: channel.trim(),
          port: port ? Number(port) : null,
          api_port: Number(apiPort) || 80,
          username: username.trim() || null,
          password: password || null,
          codec,
          force_tcp: forceTcp,
        };
        if (provider) {
          await updateProvider(provider.id, providerPayload);
        } else {
          await createProvider(providerPayload);
        }
      }

      onSaved();
      onOpenChange(false);
    } catch (err) {
      setError(extractError(err, isEdit ? 'Не удалось сохранить камеру.' : 'Не удалось создать камеру.'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Удалить камеру «${camera!.name || camera!.trassir_name}»?`)) return;
    setDeleting(true);
    setError(null);
    try {
      if (provider) await deleteProvider(provider.id);
      await deleteCamera(camera!.id);
      onSaved();
      onOpenChange(false);
    } catch (err) {
      setError(extractError(err, 'Не удалось удалить камеру.'));
      setDeleting(false);
    }
  }

  async function handleDetectResolution() {
    if (!camera) return;
    setDetectingRes(true);
    setError(null);
    try {
      const resp = await detectCameraResolution(camera.id);
      setMaxXRes(resp.data.max_x_resolution != null ? String(resp.data.max_x_resolution) : '');
      setMaxYRes(resp.data.max_y_resolution != null ? String(resp.data.max_y_resolution) : '');
    } catch (err) {
      setError(extractError(err, 'Не удалось определить разрешение.'));
    } finally {
      setDetectingRes(false);
    }
  }

  const busy = saving || detectingRes || deleting;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Редактировать камеру' : 'Добавить камеру'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-1.5">
            <Label htmlFor="uc-type">Тип источника</Label>
            <Select id="uc-type" value={type} onChange={e => setType(e.target.value)}>
              {CAMERA_TYPE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Select>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="uc-name">Название</Label>
            <Input id="uc-name" value={name} onChange={e => setName(e.target.value)} placeholder="Камера 1" />
          </div>

          {!isEdit && (
            <>
              <div className="grid gap-1.5">
                <Label htmlFor="uc-guid">GUID</Label>
                <Input
                  id="uc-guid"
                  value={guid}
                  onChange={e => setGuid(e.target.value)}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                />
                <p className="text-xs text-muted-foreground">Уникальный идентификатор камеры</p>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="uc-trassir">Trassir Name</Label>
                <Input
                  id="uc-trassir"
                  value={trassirName}
                  onChange={e => setTrassirName(e.target.value)}
                  placeholder="CAM_01"
                />
                <p className="text-xs text-muted-foreground">Уникальное имя в системе Trassir</p>
              </div>
            </>
          )}

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="uc-active"
              checked={active}
              onChange={e => setActive(e.target.checked)}
              className="cursor-pointer"
            />
            <Label htmlFor="uc-active">Камера активна</Label>
          </div>

          <div className="grid gap-1.5">
            <Label>Разрешение</Label>
            <div className="flex gap-2 items-center">
              <Input
                className="w-28"
                type="number"
                placeholder="Ширина"
                value={maxXRes}
                onChange={e => setMaxXRes(e.target.value)}
              />
              <span className="text-muted-foreground text-sm">×</span>
              <Input
                className="w-28"
                type="number"
                placeholder="Высота"
                value={maxYRes}
                onChange={e => setMaxYRes(e.target.value)}
              />
              {isEdit && (
                <Button type="button" variant="outline" size="sm" onClick={handleDetectResolution} disabled={busy}>
                  {detectingRes ? <Loader2 className="size-3 animate-spin" /> : 'Определить'}
                </Button>
              )}
            </div>
          </div>

          <div className="border-t pt-4 space-y-3">
            <div>
              <p className="text-sm font-medium">Параметры подключения</p>
              <p className="text-xs text-muted-foreground">Оставьте пустым, если подключение не настраивается сейчас</p>
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="uc-ip">IP-адрес</Label>
              <Input
                id="uc-ip"
                value={ipAddress}
                onChange={e => setIpAddress(e.target.value)}
                placeholder="192.168.1.100"
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="uc-channel">Канал</Label>
              <Input id="uc-channel" value={channel} onChange={e => setChannel(e.target.value)} placeholder="101" />
            </div>

            <div className="flex gap-2">
              <div className="flex-1 grid gap-1.5">
                <Label htmlFor="uc-port">Порт</Label>
                <Input id="uc-port" type="number" value={port} onChange={e => setPort(e.target.value)} placeholder="554" />
              </div>
              <div className="flex-1 grid gap-1.5">
                <Label htmlFor="uc-api-port">API-порт</Label>
                <Input
                  id="uc-api-port"
                  type="number"
                  value={apiPort}
                  onChange={e => setApiPort(e.target.value)}
                  placeholder="80"
                />
              </div>
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="uc-user">Логин</Label>
              <Input id="uc-user" name="camera-login" value={username} onChange={e => setUsername(e.target.value)} autoComplete="off" />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="uc-pass">Пароль</Label>
              <div className="relative">
                <Input
                  id="uc-pass"
                  name="camera-secret"
                  // NOT type=password: this is a device/system credential, not the
                  // user's login. A username+password pair Chrome offers to SAVE
                  // (undismissable on PWA mobile) even with autocomplete=new-password.
                  // Use a plain text field masked via CSS -webkit-text-security, so
                  // Chrome never treats it as a credential while it still shows dots.
                  type="text"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="pr-9"
                  autoComplete="off"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  style={{ ['WebkitTextSecurity' as keyof React.CSSProperties]: showPassword ? 'none' : 'disc' } as React.CSSProperties}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                </button>
                {loadingDetail && (
                  <Loader2 className="absolute right-9 top-1/2 size-3.5 -translate-y-1/2 animate-spin text-muted-foreground" />
                )}
              </div>
            </div>

            <div className="flex gap-4 items-end">
              <div className="grid gap-1.5 flex-1">
                <Label htmlFor="uc-codec">Кодек</Label>
                <Select id="uc-codec" value={codec} onChange={e => setCodec(e.target.value)}>
                  <option value="h264">H.264</option>
                  <option value="h265">H.265</option>
                </Select>
              </div>
              <div className="flex items-center gap-2 pb-0.5">
                <input
                  type="checkbox"
                  id="uc-tcp"
                  checked={forceTcp}
                  onChange={e => setForceTcp(e.target.checked)}
                  className="cursor-pointer"
                />
                <Label htmlFor="uc-tcp">Force TCP</Label>
              </div>
            </div>
          </div>

          {isEdit && camera && (type.startsWith('hik_') || type === 'ezviz') && isSuperuser && (
            // Параметры изображения — superuser-only until the
            // Hikvision-side fetch is rebuilt. Right now it surfaces
            // raw ConnectionResetError text when the per-jetson
            // proxy can't reach the camera (the common case on
            // Pi-onboarded branches), which scares branch operators.
            // TODO: unhide for everyone once we have a live
            // FE-rendered preview + a friendly error surface.
            <div className="border-t pt-4 space-y-4">
              {camera.admin_tunnel_capability && (
                <div className="flex justify-end">
                  <AdminTunnelButton
                    kind="cameras"
                    id={camera.id}
                    capability={camera.admin_tunnel_capability}
                  />
                </div>
              )}
              <LiveStreamPanel cameraId={camera.id} cameraType={type} />
              {type === 'ezviz' && <PtzPad cameraId={camera.id} />}
              <VendorParamsPanel cameraId={camera.id} />
            </div>
          )}

          {error && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>
          )}
        </div>

        <DialogFooter>
          {isEdit && (
            <Button variant="destructive" onClick={handleDelete} disabled={busy} className="mr-auto sm:mr-0">
              {deleting && <Loader2 className="size-3 animate-spin mr-1.5" />}
              {deleting ? 'Удаление...' : 'Удалить'}
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Отмена
          </Button>
          <Button onClick={handleSave} disabled={busy}>
            {saving && <Loader2 className="size-3 animate-spin mr-1.5" />}
            {saving ? 'Сохранение...' : isEdit ? 'Сохранить' : 'Создать'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
